import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import inventoryApi from '../../../services/inventoryApi';
import POApprovalTierRow from './POApprovalTierRow';

const CURRENCY = 'LKR';

// ─── Toggle switch ────────────────────────────────────────────────────────────

const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
      transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
      ${checked ? 'bg-indigo-600' : 'bg-gray-200'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    <span
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
        transition duration-200 ease-in-out ${checked ? 'translate-x-5' : 'translate-x-0'}`}
    />
  </button>
);

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * Inventory > Settings > PO Approval Levels
 *
 * Amount bands decide who may approve a purchase order. A purchase order's
 * grand total falls into exactly one band, and any one of that band's approvers
 * can approve it — there is no escalation between bands.
 *
 * Bands are kept contiguous by construction: adding a level splits the open
 * band at a boundary, removing one merges it into its neighbour, and only the
 * upper limit is ever directly editable.
 */
const POApprovalLevels = () => {
  const { hasPermission, isAdmin } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [errors, setErrors] = useState({});

  const [enabled, setEnabled] = useState(false);
  const [allowSuperAdminOverride, setAllowSuperAdminOverride] = useState(true);
  const [tiers, setTiers] = useState([]);

  const [addingTier, setAddingTier] = useState(false);
  const [newBoundary, setNewBoundary] = useState('');

  const canRead = hasPermission('INVENTORY:PO_APPROVAL_SETTINGS:READ') || isAdmin();
  const canEdit = hasPermission('INVENTORY:PO_APPROVAL_SETTINGS:UPDATE') || isAdmin();

  // ── Load ──────────────────────────────────────────────────────────────────
  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const res = await inventoryApi.getPOApprovalSettings();
      if (!res.success) throw new Error(res.message || 'Failed to fetch approval levels');

      setEnabled(res.data?.enabled ?? false);
      setAllowSuperAdminOverride(res.data?.allowSuperAdminOverride ?? true);
      setTiers(
        (res.data?.tiers || []).map((tier) => ({
          label: tier.label || '',
          minAmount: tier.minAmount ?? 0,
          maxAmount: tier.maxAmount ?? null,
          approvers: (tier.approvers || []).map((approver) => ({
            user: approver.user,
            userName: approver.userName || '',
            userEmail: approver.userEmail || ''
          }))
        }))
      );
    } catch (err) {
      console.error('Error loading PO approval levels:', err);
      setLoadError(err.response?.data?.message || err.message || 'Failed to load approval levels');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canRead) loadSettings();
    else setLoading(false);
  }, [canRead, loadSettings]);

  // ── Tier mutations (each preserves contiguity by construction) ─────────────

  const handleLabelChange = (index, value) => {
    setTiers((prev) => prev.map((tier, i) => (i === index ? { ...tier, label: value } : tier)));
  };

  // Changing a band's upper limit moves the next band's lower limit with it.
  const handleMaxChange = (index, value) => {
    const parsed = value === '' ? null : Number(value);
    setTiers((prev) => prev.map((tier, i) => {
      if (i === index) return { ...tier, maxAmount: parsed };
      if (i === index + 1) return { ...tier, minAmount: parsed ?? 0 };
      return tier;
    }));
  };

  // Add = split the open (last) band at a boundary.
  const handleAddTier = () => {
    const boundary = Number(newBoundary);
    const lastTier = tiers[tiers.length - 1];

    if (!Number.isFinite(boundary) || newBoundary === '') {
      toast.error('Enter the amount at which the new level starts');
      return;
    }
    if (lastTier && boundary <= lastTier.minAmount) {
      toast.error(`The boundary must be greater than ${lastTier.minAmount}`);
      return;
    }

    setTiers((prev) => {
      const next = [...prev];
      if (next.length) {
        next[next.length - 1] = { ...next[next.length - 1], maxAmount: boundary };
      }
      next.push({ label: '', minAmount: next.length ? boundary : 0, maxAmount: null, approvers: [] });
      return next;
    });

    setNewBoundary('');
    setAddingTier(false);
  };

  // Remove = merge the band into its neighbour, keeping the ladder contiguous.
  const handleRemoveTier = (index) => {
    setTiers((prev) => {
      if (prev.length <= 1) return prev;
      const next = [...prev];

      if (index === 0) {
        next[1] = { ...next[1], minAmount: 0 };
      } else {
        next[index - 1] = { ...next[index - 1], maxAmount: next[index].maxAmount };
      }

      next.splice(index, 1);
      return next;
    });
  };

  const handleApproverChange = (tierIndex, approverIndex, userId, displayName) => {
    setTiers((prev) => prev.map((tier, i) => {
      if (i !== tierIndex) return tier;

      const approvers = [...(tier.approvers || [])];
      const entry = { user: userId, userName: displayName || '', userEmail: '' };

      if (approverIndex < approvers.length) approvers[approverIndex] = entry;
      else if (userId) approvers.push(entry);

      return { ...tier, approvers };
    }));
  };

  const handleRemoveApprover = (tierIndex, approverIndex) => {
    setTiers((prev) => prev.map((tier, i) => (
      i === tierIndex
        ? { ...tier, approvers: (tier.approvers || []).filter((_, j) => j !== approverIndex) }
        : tier
    )));
  };

  // ── Validation (mirrors the server's validateTiers) ────────────────────────
  const validate = () => {
    const nextErrors = {};

    tiers.forEach((tier, index) => {
      const isLast = index === tiers.length - 1;

      if (!isLast) {
        if (tier.maxAmount === null || tier.maxAmount === undefined || tier.maxAmount === '') {
          nextErrors[`tier_${index}_max`] = 'An upper limit is required';
        } else if (Number(tier.maxAmount) <= Number(tier.minAmount)) {
          nextErrors[`tier_${index}_max`] = 'Must be greater than the lower limit';
        }
      }

      const approvers = tier.approvers || [];
      if (enabled && approvers.length === 0) {
        nextErrors[`tier_${index}_approvers`] = 'At least one approver is required while approval levels are on';
      }

      const userIds = approvers.map((a) => String(a.user));
      if (new Set(userIds).size !== userIds.length) {
        nextErrors[`tier_${index}_approvers`] = 'The same approver is listed more than once';
      }
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!validate()) {
      toast.error('Please fix the highlighted problems before saving');
      return;
    }

    try {
      setSaving(true);
      const res = await inventoryApi.updatePOApprovalSettings({
        enabled,
        allowSuperAdminOverride,
        tiers: tiers.map((tier) => ({
          label: tier.label,
          minAmount: Number(tier.minAmount) || 0,
          maxAmount: tier.maxAmount === null || tier.maxAmount === '' ? null : Number(tier.maxAmount),
          approvers: (tier.approvers || []).map((approver) => ({
            user: approver.user,
            userName: approver.userName,
            userEmail: approver.userEmail || ''
          }))
        }))
      });

      if (!res.success) throw new Error(res.message || 'Failed to save approval levels');
      toast.success('Purchase order approval levels saved');
      await loadSettings();
    } catch (err) {
      console.error('Error saving PO approval levels:', err);
      toast.error(err.response?.data?.message || err.message || 'Failed to save approval levels');
    } finally {
      setSaving(false);
    }
  };

  // ── Access guard ──────────────────────────────────────────────────────────
  if (!canRead) {
    return (
      <div className="max-w-2xl mx-auto mt-16 text-center">
        <p className="text-gray-500 text-sm">You do not have permission to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-3xl mx-auto mt-8 p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-700 text-sm">{loadError}</p>
        <button
          onClick={loadSettings}
          className="mt-3 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
        >
          Retry
        </button>
      </div>
    );
  }

  const disabled = saving || !canEdit;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">PO Approval Levels</h1>
        <p className="mt-1 text-sm text-gray-500">
          Decide who can approve a purchase order based on its value. A purchase order falls into
          exactly one level, and any one of that level&apos;s approvers can approve it.
        </p>
      </div>

      {!canEdit && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800">
            You can view these settings but not change them.
          </p>
        </div>
      )}

      {/* Master toggle */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-4">
        <div className="p-6 space-y-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-800">Enforce Approval Levels</p>
              <p className="mt-1 text-sm text-gray-500">
                When enabled, only the approvers configured below can approve or reject a purchase
                order in their amount range. When disabled, any user with the purchase order
                approval permission can approve any purchase order.
              </p>
              {enabled && (
                <p className="mt-2 text-xs text-amber-600 font-medium">
                  Applies immediately to all purchase orders awaiting approval.
                </p>
              )}
            </div>
            <div className="flex-shrink-0 pt-0.5">
              <Toggle checked={enabled} onChange={setEnabled} disabled={disabled} />
            </div>
          </div>

          <div className="flex items-start justify-between gap-4 pt-4 border-t border-gray-100">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-800">Allow Super Admin Override</p>
              <p className="mt-1 text-sm text-gray-500">
                Lets a Super Admin approve a purchase order in any range. Overrides are recorded on
                the purchase order&apos;s approval history.
              </p>
            </div>
            <div className="flex-shrink-0 pt-0.5">
              <Toggle
                checked={allowSuperAdminOverride}
                onChange={setAllowSuperAdminOverride}
                disabled={disabled}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tiers */}
      <div className="space-y-3">
        {tiers.map((tier, index) => (
          <POApprovalTierRow
            key={`tier-${index}-${tier.minAmount}`}
            tier={tier}
            index={index}
            isLast={index === tiers.length - 1}
            canRemove={tiers.length > 1 && canEdit}
            disabled={disabled}
            errors={errors}
            currency={CURRENCY}
            onLabelChange={handleLabelChange}
            onMaxChange={handleMaxChange}
            onApproverChange={handleApproverChange}
            onRemoveApprover={handleRemoveApprover}
            onRemove={handleRemoveTier}
          />
        ))}
      </div>

      {/* Add tier */}
      {canEdit && (
        <div className="mt-3">
          {addingTier ? (
            <div className="flex items-end gap-2 p-4 bg-white border border-gray-200 rounded-lg">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Start the new level at ({CURRENCY})
                </label>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={newBoundary}
                  onChange={(e) => setNewBoundary(e.target.value)}
                  placeholder="e.g. 50000"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  The current top level is split at this amount.
                </p>
              </div>
              <button
                type="button"
                onClick={handleAddTier}
                className="px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
              >
                Add
              </button>
              <button
                type="button"
                onClick={() => { setAddingTier(false); setNewBoundary(''); }}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setAddingTier(true)}
              disabled={saving}
              className="text-sm font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
            >
              + Add approval level
            </button>
          )}
        </div>
      )}

      {/* Save */}
      {canEdit && (
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={disabled}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
};

export default POApprovalLevels;
